import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, AlertTriangle, TrendingUp, Users, Building2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { fmtINR, KpiTile, BreakdownTable, PeriodAndPropertyFilter, ScopeBadge } from "@/components/app/DashboardShared";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function ManagementDashboard() {
  const [periodMonth, setPeriodMonth] = useState(currentMonth());
  const [propertyId, setPropertyId] = useState("");
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_month: periodMonth });
      if (propertyId) params.set("property_id", propertyId);
      const [res, propsRes] = await Promise.all([
        api.get(`/dashboards/payroll?${params.toString()}`),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setData(res.data);
      setProperties(propsRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [periodMonth, propertyId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const t = data.totals;
  const ac = data.attendance_compliance;
  // Manpower cost per employee
  const costPerHead = t.employees_active ? Math.round(t.total_gross / t.employees_active) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Executive view</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Management Dashboard</h1>
          <div className="mt-2 flex items-center gap-2">
            <ScopeBadge propertyId={propertyId} properties={properties} />
          </div>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Headline payroll spend, overtime trends, manpower cost per property, and operational compliance — all in one place.
          </p>
        </div>
        <PeriodAndPropertyFilter
          periodMonth={periodMonth}
          setPeriodMonth={setPeriodMonth}
          propertyId={propertyId}
          setPropertyId={setPropertyId}
          properties={properties}
        />
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Total Payout (Net)"
          value={fmtINR(t.total_net)}
          accent="green"
          sub={`Gross/CTC ${fmtINR(t.total_gross)}`}
        />
        <KpiTile
          label="Manpower Cost / Head"
          value={fmtINR(costPerHead)}
          sub={`${t.employees_active} active employee${t.employees_active === 1 ? "" : "s"}`}
        />
        <KpiTile
          label="Overtime Payout"
          value={fmtINR(t.total_ot_amount)}
          accent={t.total_ot_amount > 0 ? "amber" : undefined}
          sub={`${t.total_ot_hours} hrs total`}
        />
        <KpiTile
          label="Attendance Compliance"
          value={`${ac.compliance_pct}%`}
          accent={ac.compliance_pct >= 90 ? "green" : ac.compliance_pct >= 75 ? "amber" : "red"}
          sub={`${ac.today_present}/${t.employees_active} present today`}
        />
      </div>

      {/* Statutory + Incentive band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Employer PF" value={fmtINR(t.total_pf_employer)} />
        <KpiTile label="ESI" value={fmtINR(t.total_esi)} />
        <KpiTile label="TDS Collected" value={fmtINR(t.total_tds)} />
        <KpiTile label="Incentives Paid" value={fmtINR(t.total_incentive)} accent="blue" />
      </div>

      {/* High OT alerts */}
      <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid="mgmt-high-ot-card">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div className="label-overline">High Overtime Alerts (≥ {data.high_ot_threshold} hrs this month)</div>
        </div>
        {data.high_ot_alerts.length === 0 ? (
          <div className="py-4 text-center text-sm text-zinc-400">
            No employees crossed the {data.high_ot_threshold}-hour threshold this month.
          </div>
        ) : (
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {data.high_ot_alerts.map((r) => (
              <div key={r.user_id} className="flex items-center justify-between border border-amber-200 bg-amber-50 rounded px-3 py-2">
                <span className="text-sm font-medium">{r.name}</span>
                <span className="text-sm font-mono font-bold text-amber-700">{r.ot_hours} hrs</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cost by property + department */}
      <BreakdownTable
        title="Manpower Cost by Property"
        rows={data.by_property}
        colKey="property_name"
        colLabel="Property"
      />
      <BreakdownTable
        title="Manpower Cost by Department"
        rows={data.by_department}
        colKey="department"
        colLabel="Department"
      />

      {/* Staff strength */}
      <div className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-zinc-500" />
          <div className="label-overline">Staff Strength · by role</div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(data.staff_strength.by_role).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
            <div key={role} className="border border-zinc-200 rounded px-3 py-1.5 text-sm">
              <span className="capitalize mr-2">{role.replace("_", " ")}</span>
              <span className="font-bold font-mono">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
